import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi } from "@/hooks/use-api";
import { Label } from "@/components/ui/label";
import { Course } from "@/types/course";
import { useAppState } from "@/store/useApp";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";

interface CourseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCourseCreated?: (courseId: string) => void;
  course?: Course | null;
}

export const CourseDialog = ({
  isOpen,
  onClose,
  onCourseCreated,
  course,
}: CourseDialogProps) => {
  const [courseTitle, setCourseTitle] = useState("");
  const [courseReference, setCourseReference] = useState("");
  const [semester, setSemester] = useState("");
  const [isCreatingSemester, setIsCreatingSemester] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const api = useApi();
  const user = useAppState((state) => state.user);
  const semesters = user?.settings?.semesters || [];

  // Initialize form with course data when editing
  useEffect(() => {
    if (course) {
      setCourseTitle(course.title);
      setCourseReference(course.reference);
      setSemester(course.semester || "");
    } else {
      setCourseTitle("");
      setCourseReference("");
      setSemester("");
    }
    setIsCreatingSemester(false);
  }, [course, isOpen]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);

      if (isCreatingSemester && semester && !semesters.includes(semester)) {
        // Update user settings
        const newSemesters = [...semesters, semester];
        await api.updateSettings({ semesters: newSemesters });
      }

      if (course) {
        // Update existing course
        const updatedCourse = {
          ...course,
          title: courseTitle,
          reference: courseReference,
          semester,
        };
        await api.updateCourse(updatedCourse);
      } else {
        // Create new course
        const newCourse = await api.createCourse({
          title: courseTitle,
          reference: courseReference,
          semester,
        });
        onCourseCreated?.(newCourse.id);
      }

      onClose();
      setCourseTitle("");
      setCourseReference("");
      setSemester("");
    } catch (error) {
      console.error(`Error ${course ? "updating" : "creating"} course:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="w-[500px]"
        aria-labelledby="course-dialog-title"
        aria-describedby="course-dialog-description"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle id="course-dialog-title">
            {course ? "Edit course" : "New course"}
          </DialogTitle>
          <DialogDescription id="course-dialog-description">
            {course ? "Update course details" : "Add a new course to Claire"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="courseTitle">Title</Label>
            <Input
              id="courseTitle"
              value={courseTitle}
              onChange={(e) => setCourseTitle(e.target.value)}
              placeholder="e.g. Introduction to Computer Science"
              maxLength={100}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="courseReference">Reference</Label>
            <Input
              id="courseReference"
              value={courseReference}
              onChange={(e) => setCourseReference(e.target.value)}
              placeholder="e.g. CS101"
              maxLength={50}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="semester">Semester</Label>
            {isCreatingSemester ? (
              <div className="flex gap-2">
                <Input
                  id="semester"
                  value={semester}
                  onChange={(e) => setSemester(e.target.value)}
                  placeholder="e.g. Fall 2025"
                  className="flex-1"
                  autoFocus
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setIsCreatingSemester(false);
                    setSemester("");
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Select
                value={semester}
                onValueChange={(value) => {
                  if (value === "__create__") {
                    setIsCreatingSemester(true);
                    setSemester("");
                  } else {
                    setSemester(value);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select semester" />
                </SelectTrigger>
                <SelectContent>
                  {semesters.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                  <SelectItem
                    value="__create__"
                    className="text-muted-foreground font-medium"
                  >
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Create new semester...
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              aria-label={course ? "Update course" : "Add course"}
              disabled={!courseTitle || !courseReference || isSubmitting}
            >
              {isSubmitting
                ? course
                  ? "Updating..."
                  : "Creating..."
                : course
                ? "Update course"
                : "Add course"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
